<script setup lang="ts">
import { ref } from "vue";
import InlineEmbed from "../embed/InlineEmbed.vue";
import PopupEmbed from "../embed/PopupEmbed.vue";
import InlineButtonEmbed from "../embed/InlineButtonEmbed.vue";
import ElementClickEmbed from "../embed/ElementClickEmbed.vue";

interface Props {
    token: string;
    eventType?: { isTeamEventType?: boolean };
    embedType?: "group";
}

const props = defineProps<Props>();

type EmbedType = "inline" | "popup" | "inline-button" | "element" | null;

const selectedEmbed = ref<EmbedType>(null);

const embedOptions = [
    {
        id: "inline" as const,
        titleKey: "event_types.share_hub.embed.inline.title",
        descriptionKey: "event_types.share_hub.embed.inline.description",
    },
    {
        id: "popup" as const,
        titleKey: "event_types.share_hub.embed.popup.title",
        descriptionKey: "event_types.share_hub.embed.popup.description",
    },
    {
        id: "inline-button" as const,
        titleKey: "event_types.share_hub.embed.inline_button.title",
        descriptionKey: "event_types.share_hub.embed.inline_button.description",
    },
    {
        id: "element" as const,
        titleKey: "event_types.share_hub.embed.element.title",
        descriptionKey: "event_types.share_hub.embed.element.description",
    },
];

function goBack() {
    selectedEmbed.value = null;
}
</script>

<template>
    <div>
        <!-- Embed type selection grid -->
        <div v-if="!selectedEmbed" class="gap-3 grid grid-cols-2">
            <button
                v-for="option in embedOptions"
                :key="option.id"
                @click="selectedEmbed = option.id"
                class="group rounded-lg border-iron-200 hover:border-z-primary-300 hover:bg-z-primary-25 overflow-hidden border text-left transition-all"
            >
                <!-- SVG Preview Image -->
                <div class="flex items-center justify-center">
                    <!-- Inline Embed Preview -->
                    <svg
                        v-if="option.id === 'inline'"
                        viewBox="0 0 340 185"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-auto w-full"
                    >
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="155"
                            rx="12"
                            fill="#F9FAFB"
                            stroke="#E5E7EB"
                            stroke-width="2"
                        />
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="35"
                            rx="12"
                            fill="#F3F4F6"
                        />
                        <rect
                            x="20"
                            y="38"
                            width="300"
                            height="12"
                            fill="#F3F4F6"
                        />
                        <circle cx="45" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="65" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="85" cy="32" r="6" fill="#D1D5DB" />
                        <rect
                            x="110"
                            y="24"
                            width="190"
                            height="16"
                            rx="4"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="65"
                            width="260"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="85"
                            width="200"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="115"
                            width="260"
                            height="40"
                            rx="6"
                            fill="#E5E7EB"
                            stroke="#D1D5DB"
                            stroke-width="2"
                        />
                    </svg>

                    <!-- Popup Button Preview -->
                    <svg
                        v-else-if="option.id === 'popup'"
                        viewBox="0 0 340 185"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-auto w-full"
                    >
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="155"
                            rx="12"
                            fill="#F9FAFB"
                            stroke="#E5E7EB"
                            stroke-width="2"
                        />
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="35"
                            rx="12"
                            fill="#F3F4F6"
                        />
                        <rect
                            x="20"
                            y="38"
                            width="300"
                            height="12"
                            fill="#F3F4F6"
                        />
                        <circle cx="45" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="65" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="85" cy="32" r="6" fill="#D1D5DB" />
                        <rect
                            x="110"
                            y="24"
                            width="190"
                            height="16"
                            rx="4"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="65"
                            width="260"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="85"
                            width="200"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="105"
                            width="230"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="125"
                            width="160"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="220"
                            y="140"
                            width="80"
                            height="18"
                            rx="4"
                            fill="#374151"
                        />
                    </svg>

                    <!-- Embedded Button Preview -->
                    <svg
                        v-else-if="option.id === 'inline-button'"
                        viewBox="0 0 340 185"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-auto w-full"
                    >
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="155"
                            rx="12"
                            fill="#F9FAFB"
                            stroke="#E5E7EB"
                            stroke-width="2"
                        />
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="35"
                            rx="12"
                            fill="#F3F4F6"
                        />
                        <rect
                            x="20"
                            y="38"
                            width="300"
                            height="12"
                            fill="#F3F4F6"
                        />
                        <circle cx="45" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="65" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="85" cy="32" r="6" fill="#D1D5DB" />
                        <rect
                            x="110"
                            y="24"
                            width="190"
                            height="16"
                            rx="4"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="65"
                            width="260"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="85"
                            width="200"
                            height="10"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="120"
                            width="120"
                            height="30"
                            rx="6"
                            fill="#9CA3AF"
                        />
                    </svg>

                    <!-- Custom Element Preview -->
                    <svg
                        v-else-if="option.id === 'element'"
                        viewBox="0 0 340 185"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-auto w-full"
                    >
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="155"
                            rx="12"
                            fill="#F9FAFB"
                            stroke="#E5E7EB"
                            stroke-width="2"
                        />
                        <rect
                            x="20"
                            y="15"
                            width="300"
                            height="35"
                            rx="12"
                            fill="#F3F4F6"
                        />
                        <rect
                            x="20"
                            y="38"
                            width="300"
                            height="12"
                            fill="#F3F4F6"
                        />
                        <circle cx="45" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="65" cy="32" r="6" fill="#D1D5DB" />
                        <circle cx="85" cy="32" r="6" fill="#D1D5DB" />
                        <rect
                            x="110"
                            y="24"
                            width="190"
                            height="16"
                            rx="4"
                            fill="#E5E7EB"
                        />
                        <rect
                            x="40"
                            y="60"
                            width="140"
                            height="100"
                            rx="6"
                            fill="white"
                            stroke="#E5E7EB"
                            stroke-width="2"
                        />
                        <rect
                            x="52"
                            y="72"
                            width="116"
                            height="12"
                            rx="3"
                            fill="#E5E7EB"
                        />
                        <g fill="#374151">
                            <circle cx="64" cy="100" r="5" />
                            <circle cx="84" cy="100" r="5" />
                            <circle cx="104" cy="100" r="5" />
                            <circle cx="124" cy="100" r="5" />
                            <circle cx="144" cy="100" r="5" />
                            <circle cx="64" cy="120" r="5" />
                            <circle cx="84" cy="120" r="5" />
                            <circle cx="104" cy="120" r="5" />
                            <circle cx="124" cy="120" r="5" />
                            <circle cx="144" cy="120" r="5" />
                            <circle cx="64" cy="140" r="5" />
                            <circle cx="84" cy="140" r="5" />
                            <circle cx="104" cy="140" r="5" />
                            <circle cx="124" cy="140" r="5" />
                            <circle cx="144" cy="140" r="5" />
                        </g>
                        <rect
                            x="96"
                            y="112"
                            width="18"
                            height="18"
                            rx="3"
                            fill="#1F2937"
                        />
                        <rect
                            x="200"
                            y="70"
                            width="100"
                            height="30"
                            rx="5"
                            stroke="#9CA3AF"
                            stroke-width="2"
                            stroke-dasharray="6 4"
                            fill="none"
                        />
                        <rect
                            x="215"
                            y="81"
                            width="70"
                            height="8"
                            rx="2"
                            fill="#D1D5DB"
                        />
                    </svg>
                </div>

                <!-- Title and Description -->
                <div class="p-4">
                    <h3 class="font-medium text-iron-900">
                        {{ $t(option.titleKey) }}
                    </h3>
                    <p class="mt-1 text-sm text-iron-500">
                        {{ $t(option.descriptionKey) }}
                    </p>
                </div>
            </button>
        </div>

        <!-- Selected embed view -->
        <div v-else>
            <InlineEmbed
                v-if="selectedEmbed === 'inline'"
                :token="token"
                :embed-type="embedType"
                @back="goBack"
            />
            <PopupEmbed
                v-else-if="selectedEmbed === 'popup'"
                :token="token"
                :event-type="eventType"
                :embed-type="embedType"
                @back="goBack"
            />
            <InlineButtonEmbed
                v-else-if="selectedEmbed === 'inline-button'"
                :token="token"
                :event-type="eventType"
                :embed-type="embedType"
                @back="goBack"
            />
            <ElementClickEmbed
                v-else-if="selectedEmbed === 'element'"
                :token="token"
                :event-type="eventType"
                :embed-type="embedType"
                @back="goBack"
            />
        </div>
    </div>
</template>
